import { ArrowBigLeft } from 'lucide-react';
import { ReactElement, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormMessage, Inline } from '@crossnokaye/ux-common';
import { Stack, Typography, withTooltip } from '@crossnokaye/ux-common';
import { Input } from '@crossnokaye/ux-common/ckds';

import { EditSettingsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

const InputWithTooltip = withTooltip(Input);

interface SettingsFieldInputProps {
  setting: EnhancedSetting;
  form: UseFormReturn<EditSettingsFormData>;
  /**
   * Required form path — see `SettingFieldSwitch` for why this is required
   * (`settings.<id>` vs `sequencerSettings.<id>` is owned by the dispatch
   * layer).
   */
  name: `settings.${string}` | `sequencerSettings.${string}`;
}

/**
 * Input field component for text/number settings.
 * Shows change indicator when value differs from original.
 */
export const SettingFieldInput = ({
  setting,
  form,
  name,
}: SettingsFieldInputProps): ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  const sourceLabels = useMemo(() => {
    // If setting.units is not configured, assume the value is already in displayUnits
    const sourceUnits = getSourceUnits(setting);
    return formatUnits({ value: setting?.value, units: sourceUnits }, setting?.displayUnits);
  }, [setting]);

  const hasChanged = useFieldIsDirty(form.control, name);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        let units = sourceLabels.unitsLabel?.toUpperCase();

        if (sourceLabels.unitsLabel?.toUpperCase() === 'INHG') {
          units = 'PSIG';
        }

        return (
          <FormItem>
            <FormControl>
              <InputWithTooltip
                label={setting.label}
                info={
                  <Stack className="max-w-96">
                    <Typography variant="h4">{setting.label}</Typography>
                    <Typography variant="body">{setting.description}</Typography>
                  </Stack>
                }
                infoTabStop={false}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
                disabled={disabledByPermissions}
                className={hasChanged ? 'text-ckds-input-fg-new' : ''}
                tooltipContent={disabledByPermissions ? tooltipText : undefined}
                inlineLabel={
                  hasChanged && sourceLabels.valueLabel ? (
                    <Inline gap="thin" data-component="InputInlineValueChangeLabel">
                      <Inline className="text-ckds-input-fg-new" gap="thin">
                        {units} <ArrowBigLeft />
                      </Inline>
                      {sourceLabels.valueLabel} {sourceLabels.unitsLabel?.toUpperCase()}
                    </Inline>
                  ) : (
                    sourceLabels.unitsLabel?.toUpperCase()
                  )
                }
              />
            </FormControl>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
